import type { LucideIcon } from 'lucide-react'

import { formatCurrency } from '@/lib/utils'

import {
  PAYMENT_METHODS,
  type PaymentMethodBreakdown as PaymentMethodBreakdownData,
  hasNonZeroMethods,
} from './cashbox-constants'

type Variant = 'cash' | 'credit' | 'deposit' | 'info'

const VARIANT_STYLES: Record<
  Variant,
  { border: string; bg: string; text: string; divider: string }
> = {
  cash: {
    border: 'border-emerald-200 dark:border-emerald-800/40',
    bg: 'bg-emerald-50/60 dark:bg-emerald-950/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    divider: 'border-emerald-100 dark:border-emerald-800/30',
  },
  credit: {
    border: 'border-amber-200 dark:border-amber-800/40',
    bg: 'bg-amber-50/60 dark:bg-amber-950/30',
    text: 'text-amber-600 dark:text-amber-400',
    divider: 'border-amber-100 dark:border-amber-800/30',
  },
  deposit: {
    border: 'border-sky-200 dark:border-sky-800/40',
    bg: 'bg-sky-50/60 dark:bg-sky-950/30',
    text: 'text-sky-600 dark:text-sky-400',
    divider: 'border-sky-100 dark:border-sky-800/30',
  },
  info: {
    border: 'border-slate-200 dark:border-slate-700/40',
    bg: 'bg-slate-50/60 dark:bg-slate-900/30',
    text: 'text-slate-600 dark:text-slate-300',
    divider: 'border-slate-100 dark:border-slate-700/30',
  },
}

interface Props {
  title: string
  total: number
  breakdown?: PaymentMethodBreakdownData
  variant: Variant
  icon?: LucideIcon
  hint?: string
  signed?: boolean
}

export function PaymentMethodBreakdown({
  title,
  total,
  breakdown,
  variant,
  icon: Icon,
  hint,
  signed,
}: Props) {
  const styles = VARIANT_STYLES[variant]
  const showMethods = breakdown && hasNonZeroMethods(breakdown)
  const formattedTotal = formatCurrency(total)
  const amountLabel = signed && total > 0 ? `+${formattedTotal}` : formattedTotal

  return (
    <div className={`rounded-md border ${styles.border} ${styles.bg} overflow-hidden`}>
      <div className="flex justify-between items-center p-2.5">
        <span className="text-sm text-muted-foreground flex items-center gap-1.5">
          {Icon && <Icon className={`h-3.5 w-3.5 ${styles.text}`} />}
          {title}
        </span>
        <span className={`font-semibold ${styles.text}`}>{amountLabel}</span>
      </div>
      {hint && (
        <div className={`px-2.5 pb-2 text-xs text-muted-foreground/80 border-t ${styles.divider} pt-1.5`}>
          {hint}
        </div>
      )}
      {showMethods && breakdown && (
        <div className={`px-2.5 pb-2 space-y-0.5 border-t ${styles.divider} pt-1.5`}>
          {PAYMENT_METHODS.map((method) => {
            const amount = breakdown[method.key] || 0
            if (amount < 0.01) return null
            const MethodIcon = method.icon
            return (
              <div
                key={method.key}
                className="flex justify-between items-center pl-5 sm:pl-7 pr-0.5 py-0.5"
              >
                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <MethodIcon className="h-3 w-3 shrink-0" />
                  {method.label}
                </span>
                <span className="text-xs font-medium text-foreground/80">
                  {formatCurrency(amount)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
